package org.multiplatform.kotfolio.pages.coroutines

import androidx.compose.runtime.*
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.*
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ul

@Page("/coroutines/flow_combine")
@Composable
fun FlowCombinePage() {
    BlogLayout(
        PageProperties(
            "Combine Flow",
            ""
        ),
        BlogProperties("Coroutines", "Combine Flow")
    ) {
        Column(
            horizontalAlignment = Alignment.Start
        ) {
            SpanText(
                text = "Combine Flow",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "merge: top-level function combines multiple Flows of the same type into one",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "zip: pairs data from the first Flow to the second Flow using the function you specified.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "combine: pairs like zip but uses the most recent value emitted by each flow, and continue to run as long as a Flow emits a value.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "zip() Use",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Emits only when both flows have values (one-time use case, like fetching APIs).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "combine() Use",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The combine operator’s role is to take multiple independent flows (async data streams) and merge them into single flow. This result flow, emits values, that are derived from the latest values of the input flows. combine is very useful in reactive programming because it allows you to react to the latest set of data from several sources (several flows). For Example — If you have user input from one flow and data from a network request from another, combine could be used to update the UI when either of those data sources changes. combine operates on the most recent values from all other input flows and then applies a transformation function to the produce a new output value.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            //https://github.com/puriakazemieh/Shop-Kotlin-kmp.git
            KotlinCode(
                code = """
                    object CartEventBus {
                        private val _events = MutableSharedFlow<Unit>(extraBufferCapacity = 1)
                        val events: SharedFlow<Unit> = _events.asSharedFlow()

                        fun refresh() {
                            _events.tryEmit(Unit)
                        }
                    }
                    
                    sealed class AppResult<out T> {
                        data class Success<T>(val data: T) : AppResult<T>()
                        data class Error(val message: Any, val code: Int? = null) : AppResult<Nothing>()
                        data object Loading : AppResult<Nothing>()
                    }


                    inline fun <T, R> AppResult<T>.doOnSuccess(func: (AppResult: T) -> R): AppResult<T> {
                        if (this is AppResult.Success) {
                            func.invoke(data)
                        }
                        return this
                    }


                    inline fun <T, R> AppResult<T>.doOnError(func: (error: Any?) -> R): AppResult<T> {
                        if (this is AppResult.Error) {
                            func.invoke(message)
                        }
                        return this
                    }

                    fun <T> AppResult<T>.getSuccessValue(): T? {
                        if (this is AppResult.Success)
                            return data
                        return null
                    }
                    
                    data class Cart(
                        val items: List<CartItem>,
                        val savedForLater: List<CartItem> = emptyList(),
                        val subtotal: Double,
                        val totalQty: Int,
                        val discountAmount: Double = 0.0,
                        val total: Double = subtotal,
                        val appliedDiscountCode: String? = null
                    )

                    data class CartItem(
                        val id: Long,
                        val variantId: Long,
                        val qty: Int,
                        val productId: Long,
                        val productTitle: String,
                        val productSlug: String,
                        val imageUrl: String?,
                        val options: Map<String, String>,
                        val price: Double,
                        val compareAtPrice: Double?,
                        val availableQty: Int,
                        val savedForLater: Boolean = false,
                        val isActive: Boolean,
                        val lineTotal: Double
                    )
                    
                    class CartViewModel(
                        private val getCartUseCase: GetCartUseCase,
                        private val removeFromCartUseCase: RemoveFromCartUseCase,
                        private val adjustCartVariantQtyUseCase: AdjustCartVariantQtyUseCase,
                        private val moveToSaveForLaterUseCase: MoveToSaveForLaterUseCase,
                        private val moveToCartUseCase: MoveToCartUseCase,
                        private val applyDiscountUseCase: ApplyDiscountUseCase,
                        private val removeDiscountUseCase: RemoveDiscountUseCase
                    ) : ViewModel() {

                        private val _state = MutableStateFlow(CartState())
                        val state = _state.asStateFlow()

                        private val _effect = Channel<CartEffect>()
                        val effect = _effect.receiveAsFlow()

                        init {
                            observeCart()
                        }

                        @OptIn(kotlinx.coroutines.ExperimentalCoroutinesApi::class)
                        private fun observeCart() {
                            viewModelScope.launch {
                                merge(flowOf(Unit), CartEventBus.events)
                                    .flatMapLatest { getCartUseCase() }
                                    .collect { result ->
                                        _state.update { it.copy(cartState = result) }
                                    }
                            }
                        }

                        fun handleIntent(intent: CartIntent) {
                            when (intent) {
                                is CartIntent.Refresh -> refreshCart()
                                is CartIntent.AdjustQuantity -> adjustQuantity(intent.variantId, intent.delta)
                                is CartIntent.DeleteItem -> deleteCartItem(intent.itemId)
                                is CartIntent.MoveToSaveForLater -> moveToSaveForLater(intent.itemId)
                                is CartIntent.MoveToCart -> moveToCart(intent.itemId)
                                is CartIntent.ApplyDiscount -> applyDiscount(intent.code)
                                is CartIntent.RemoveDiscount -> removeDiscount()
                            }
                        }

                        private fun applyDiscount(code: String) {
                            if (code.isBlank()) {
                                viewModelScope.launch {
                                    _effect.send(CartEffect.ShowError(com.kazemieh.designsystem.Resources.String.InvalidInput))
                                }
                                return
                            }

                            viewModelScope.launch {
                                _state.update { it.copy(isApplyingDiscount = true) }
                                when (val result = applyDiscountUseCase(code.trim())) {
                                    is AppResult.Success -> {
                                        _state.update { it.copy(cartState = result, isApplyingDiscount = false) }
                                    }

                                    is AppResult.Error -> {
                                        _effect.send(CartEffect.ShowError(result.message))
                                        _state.update { it.copy(isApplyingDiscount = false) }
                                    }

                                    else -> {
                                        _state.update { it.copy(isApplyingDiscount = false) }
                                    }
                                }
                            }
                        }

                        private fun removeDiscount() {
                            viewModelScope.launch {
                                _state.update { it.copy(isApplyingDiscount = true) }
                                when (val result = removeDiscountUseCase()) {
                                    is AppResult.Success -> {
                                        _state.update { it.copy(cartState = result, isApplyingDiscount = false) }
                                    }

                                    is AppResult.Error -> {
                                        _effect.send(CartEffect.ShowError(result.message))
                                        _state.update { it.copy(isApplyingDiscount = false) }
                                    }

                                    else -> {
                                        _state.update { it.copy(isApplyingDiscount = false) }
                                    }
                                }
                            }
                        }

                        private fun refreshCart() {
                            viewModelScope.launch {
                                _state.update { it.copy(isRefreshing = true) }
                                getCartUseCase().collect { result ->
                                    _state.update { it.copy(cartState = result, isRefreshing = false) }
                                }
                            }
                        }

                        private fun deleteCartItem(itemId: Long) {
                            viewModelScope.launch {
                                when (val result = removeFromCartUseCase(itemId)) {
                                    is AppResult.Success -> {
                                        _state.update { it.copy(cartState = result) }
                                    }
                                    is AppResult.Error -> {
                                        _effect.send(CartEffect.ShowError(result.message))
                                    }
                                    else -> {}
                                }
                            }
                        }

                        private fun adjustQuantity(variantId: Long, delta: Int) {
                            viewModelScope.launch {
                                when (val result = adjustCartVariantQtyUseCase(variantId, delta)) {
                                    is AppResult.Success -> {
                                        _state.update { it.copy(cartState = result) }
                                    }
                                    is AppResult.Error -> {
                                        _effect.send(CartEffect.ShowError(result.message))
                                    }
                                    else -> {}
                                }
                            }
                        }

                        private fun moveToSaveForLater(itemId: Long) {
                            viewModelScope.launch {
                                _state.update { it.copy(isRefreshing = true) }
                                when (val result = moveToSaveForLaterUseCase(itemId)) {
                                    is AppResult.Success -> {
                                        _state.update { it.copy(cartState = result, isRefreshing = false) }
                                    }
                                    is AppResult.Error -> {
                                        _effect.send(CartEffect.ShowError(result.message))
                                        _state.update { it.copy(isRefreshing = false) }
                                    }
                                    else -> {
                                        _state.update { it.copy(isRefreshing = false) }
                                    }
                                }
                            }
                        }

                        private fun moveToCart(itemId: Long) {
                            viewModelScope.launch {
                                _state.update { it.copy(isRefreshing = true) }
                                when (val result = moveToCartUseCase(itemId)) {
                                    is AppResult.Success -> {
                                        _state.update { it.copy(cartState = result, isRefreshing = false) }
                                    }
                                    is AppResult.Error -> {
                                        _effect.send(CartEffect.ShowError(result.message))
                                        _state.update { it.copy(isRefreshing = false) }
                                    }
                                    else -> {
                                        _state.update { it.copy(isRefreshing = false) }
                                    }
                                }
                            }
                        }
                    }

                    data class CartState(
                        val cartState: AppResult<Cart> = AppResult.Loading,
                        val isRefreshing: Boolean = false,
                        val isApplyingDiscount: Boolean = false
                    )

                    sealed interface CartIntent {
                        data object Refresh : CartIntent
                        data class AdjustQuantity(val variantId: Long, val delta: Int) : CartIntent
                        data class DeleteItem(val itemId: Long) : CartIntent
                        data class MoveToSaveForLater(val itemId: Long) : CartIntent
                        data class MoveToCart(val itemId: Long) : CartIntent
                        data class ApplyDiscount(val code: String) : CartIntent
                        data object RemoveDiscount : CartIntent
                    }

                    sealed interface CartEffect {
                        data class ShowError(val message: Any) : CartEffect
                    }
                    """.trimIndent()
            )

            //https://github.com/felix-leyva/SimpleViewModelExample.git (combine 3 category selection)
            KotlinCode(
                code = """
                    //Combine using Brand - Category Selection 
                    
                    val gatewayModule = module {
                        single { BlogsRepository() } withOptions { createdAtStart() }
                        factoryOf(::BlogDiscoveryUIModel)

                        single<GetLanguageUseCase> { GetLanguageUseCase { get<BlogsRepository>().getLanguage() } }
                        single<GetTopicForLanguageUseCase> { GetTopicForLanguageUseCase { get<BlogsRepository>().getTopic(it) } }
                        single<GetTracksForTopicUseCase> { GetTracksForTopicUseCase { get<BlogsRepository>().getTrack(it) } }

                        single<CoroutineScope> {
                            CoroutineScope(SupervisorJob())
                        }
                    }

                    data class Language(val id: String, val name: String)
                    data class Topic(val id: String, val name: String, val languageId: String)

                    @Immutable
                    data class Blog(
                        val index: Int,
                        val totalView: String,
                        val links: List<Pair<String, String>> = emptyList(),
                        override val blogEnum: BlogEnum)
                        : BlogBaseEntity(blogEnum)
                    
                    open class BlogBaseEntity(open val blogEnum: BlogEnum)
                    
                    enum class BlogEnum(
                        val description: String,
                        val tags: List<String> = emptyList(),
                        val linkTags: List<Pair<String, String>> = emptyList(),
                        val bulletPoints: List<String> = emptyList(),
                        val fromTo: String
                    ) {
                        //Android Studio
                        GradleAndroidStudio(
                            fromTo = "5-Jun-2026",
                            description = "Gradle is a build automation tool. Its job is to take your source code and turn it into a final output — an APK for Android, a JAR for a library, or an executable for a backend service.",
                            tags = listOf(
                                "Gradle",
                                "Phase",
                                "toml file",
                                "Gradle Plugin",
                                "Compiler Plugin",
                                "gradle.properties"
                            ),
                            bulletPoints = listOf("Gradle (Phase, settings file, build.gradle (project root), Gradle Plugin, Gradle Catalog sections, toml file), gradle.properties")
                        ),
                    }
                    
                    class BlogsRepository {
                        private val languageList = listOf(
                            Language("programming", "Programming"),
                        )

                        private val topicList = listOf(
                            //PROGRAMMING
                            Topic("android_studio", "Android Studio", "programming"),
                        )

                        private val tracks = persistentMapOf(
                            "android_studio" to listOf(
                                Blog(
                                    1,
                                    "",
                                    links = listOf("Gradle" to Constants.GRADLE_URL),
                                    blogEnum = BlogEnum.GradleAndroidStudio
                                ),
                            ),
                        )
                        
                        fun getLanguage(): Flow<ImmutableList<Language>> = flow {
                            emit(languageList.toImmutableList())
                        }

                        fun getTopic(languageId: String): Flow<ImmutableList<Topic>> = flow {
                            emit(topicList.asSequence().filter { it.languageId == languageId }.toImmutableList())
                        }

                        fun getTrack(topicId: String): Flow<ImmutableList<Blog>> = flow {
                            emit(tracks[topicId]?.toImmutableList() ?: persistentListOf())
                        }
                    }
                    
                    interface UIModel<UIState, UICommand> {
                        val scope: CoroutineScope
                        val uiState: StateFlow<UIState>
                        fun sendCommand(command: UICommand)
                    }
                    
                    data class BlogDiscoveryUIState(
                        val languages: ImmutableList<Language>,
                        val selectedLanguage: Language?,
                        val topics: ImmutableList<Topic>,
                        val selectedTopic: Topic?,
                        val tracks: ImmutableList<Blog>,
                    ) {
                        companion object {
                            val Default = BlogDiscoveryUIState(
                                languages = persistentListOf(),
                                selectedLanguage = null,
                                topics = persistentListOf(),
                                selectedTopic = null,
                                tracks = persistentListOf()
                            )
                        }
                    }

                    sealed interface BlogDiscoveryCommand {
                        data class SelectLanguage(val language: Language) : BlogDiscoveryCommand
                        data class SelectTopic(val topic: Topic) : BlogDiscoveryCommand
                    }
                    
                    @OptIn(ExperimentalCoroutinesApi::class)
                    class BlogDiscoveryUIModel(
                        override val scope: CoroutineScope,
                        getLanguageUseCase: GetLanguageUseCase,
                        getTopicForLanguageUseCase: GetTopicForLanguageUseCase,
                        getTracksForTopicUseCase: GetTracksForTopicUseCase
                    ) : UIModel<BlogDiscoveryUIState, BlogDiscoveryCommand> {
                        private val languages = getLanguageUseCase()

                        // --- Internal mutable state (user selections) ---
                        private val selectLanguage = MutableStateFlow<Language?>(null)
                        private val selectTopic = MutableStateFlow<Topic?>(null)

                        private val topics = selectLanguage.flatMapLatest { language ->
                            language?.let { getTopicForLanguageUseCase(it.id) }
                                ?: flowOf(persistentListOf())
                        }

                        private val tracks = selectTopic.flatMapLatest { topic ->
                            topic?.let { getTracksForTopicUseCase(it.id) }
                                ?: flowOf(persistentListOf())
                        }

                        // --- Public state: combine wires everything together ---
                        override val uiState: StateFlow<BlogDiscoveryUIState> = combine(
                            languages,
                            selectLanguage,
                            topics,
                            selectTopic,
                            tracks,
                            ::BlogDiscoveryUIState
                        ).stateIn(scope, SharingStarted.WhileSubscribed(5_000), BlogDiscoveryUIState.Default)

                        // --- Commands: update selections, reset downstream when parent changes ---
                        override fun sendCommand(command: BlogDiscoveryCommand) {
                            when (command) {
                                is BlogDiscoveryCommand.SelectLanguage -> {
                                    selectLanguage.value = command.language
                                    selectTopic.value = null
                                }

                                is BlogDiscoveryCommand.SelectTopic -> {
                                    selectTopic.value = command.topic
                                }
                            }
                        }
                    }
                    
                    //usecase
                    fun interface GetLanguageUseCase {
                        operator fun invoke(): Flow<ImmutableList<Language>>
                    }
                    
                    fun interface GetTopicForLanguageUseCase {
                        operator fun invoke(genreId: String): Flow<ImmutableList<Topic>>
                    }
                    
                    fun interface GetTracksForTopicUseCase {
                        operator fun invoke(albumId: String): Flow<ImmutableList<Blog>>
                    }
                    
                    //First Way
                    @OptIn(DelicateApi::class)
                    @Composable
                    fun BlogSection(
                        blogDiscoveryUIModel: BlogDiscoveryUIModel = getKoin().get()
                    ) {
                        val state by blogDiscoveryUIModel.uiState.collectAsState()
                        
                         if (state.languages.isNotEmpty()) { }
                        
                         if (state.topics.isNotEmpty()) { }
                         
                         if (state.tracks.isNotEmpty()) { }
                    }
                    
                    //Second Way 
                    val uiModel = remember { GlobalContext.get().get<BlogDiscoveryUIModel>() }
                    val state by uiModel.uiState.collectAsState()
                    
                    BlogSection(
                        state = state,
                        sendCommand = uiModel::sendCommand,
                    )
                    
                    @OptIn(DelicateApi::class)
                    @Composable
                    fun BlogSection(
                        state: BlogDiscoveryUIState,
                        sendCommand: (BlogDiscoveryCommand) -> Unit
                    ) { 
                           //sendCommand(BlogDiscoveryCommand.SelectLanguage(tag))
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = $$"""
                    interface ApiService {
                        @GET("user/{id}")
                        suspend fun getUser(@Path("id") userId: Int): User

                        @GET("posts/{userId}")
                        suspend fun getUserPosts(@Path("userId") userId: Int): List<Post>
                    }
                    
                    class UserRepository(private val apiService: ApiService) {

                        fun getUserWithPosts(userId: Int): Flow<Pair<User, List<Post>>> {
                            val userFlow = flow { emit(apiService.getUser(userId)) }
                            val postsFlow = flow { emit(apiService.getUserPosts(userId)) }

                            // Combine both flows and return a Pair
                            return userFlow.zip(postsFlow) { user, posts ->
                                user to posts  // Return a Pair<User, List<Post>>
                            }
                        }
                    }
                    
                    class UserViewModel(private val repository: UserRepository) : ViewModel() {

                        fun fetchUserWithPosts(userId: Int) {
                            viewModelScope.launch {
                                repository.getUserWithPosts(userId)
                                    .catch { e -> Log.e("Error", "Error fetching data: ${e.message}") }
                                    .collect { (user, posts) ->
                                        Log.d("Result", "User: ${user.name}, Posts: ${posts.size}")
                                    }
                            }
                        }
                    }
                    
                    return userFlow.combine(postsFlow) { user, posts ->
                        user to posts  // Returns updated results whenever one flow updates
                    } 
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //https://github.com/MostafaMohamed2002/GameVault
                    //return@flow
                    
                    class FreeGamesRepositoryImpl @Inject constructor(
                        private val api: FreeGamesApi,
                        private val db: FreeGamesDao,
                        private val gameDetailsDao : FreeGameDetailsDao
                    ) : FreeGamesRepository {
                        override suspend fun getGames(): Flow<ResultWrapper<List<Game>>> {
                            return flow {
                                emit(ResultWrapper.Loading())
                                val cachedGames = db.getGames().first()
                                if (cachedGames.isNotEmpty()) {
                                    emit(ResultWrapper.Success(cachedGames.map { it.toDomain() }))
                                    return@flow
                                } else {
                                    try {
                                        val newtworkGames = api.getGames()
                                        if (newtworkGames.isSuccessful) {
                                            val games = newtworkGames.body()!!.map { it.toEntity() }
                                            db.upsertAll(games)
                                            emit(ResultWrapper.Success(games.map { it.toDomain() }))
                                        }
                                    } catch (e: HttpException) {
                                        emit(ResultWrapper.Error(message = e.message))
                                    } catch (e: Exception) {
                                        emit(ResultWrapper.Error(message = e.message))
                                    }
                                }
                            }
                        }
                    }  
                    
                    @Dao
                    interface FreeGamesDao {
                        @Upsert
                        suspend fun upsertAll(games: List<FreeGamesEntity>)
                    }
                    
                    interface FreeGamesApi {
                        @GET("games/")
                        suspend fun getGames(): Response<List<GameDTO>>
                    }
                    """.trimIndent()
            )

            //https://www.linkedin.com/in/mikkareem/
            KotlinCode(
                code = """
                    //transform to create custom operators using duplicates remove
                    
                    //1
                    fun <T> Flow<T>. dropDuplicates(): Flow<T> {
                        val unique = mutableSetOf<T>()

                        return transform { element ->
                            if (unique.add(element)) {
                                emit (element)
                            }
                        }
                    }
                    
                    //2
                    fun <T> Flow<T>.dropDuplicates(): Flow<T> = flow {
                        val seen = mutableSetOf<T>()
                        collect { value ->
                            if (seen.add(value)) emit(value)
                        }
                    }

                    fun main() {
                        val numbersFlow = flowOf (1,2,1,2,3,4,5, 2,5,6,7,8,1,5,9,10,4,6)

                        runBlocking {
                            numbersFlow
                                .onEach { delay(1000) }
                                .dropDuplicates() // Use here
                                •toList()
                                •let { println(it) }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @OptIn(ExperimentalContracts::class, ExperimentalExtendedContracts:: class)
                    fun <T: R, R> T.letIf(condition: Boolean, block: (T) -> R): R {
                        contract {
                            callsInPlace(block, InvocationKind.AT_MOST_ONCE)
                            condition.holdsIn(block)
                        ｝
                        return if (condition) ‹
                            block(this)
                        } else {
                            this
                        ｝
                    ｝
                    
                    val newFlow: Flow<Post> = postsFlow
                    •letIf(requiredTags != null) { it.filter { it.tags.containsAll(requiredTags) } }
                    .letIf(showOnlyImportant） ｛ it.filter ｛ it.important ｝ ｝
                    
                    // Compose
                    val modifier = Modifier
                        •letIf(onClick!= null) { it.clickable(onClick = onclick) }
                        •letIf(isError) { it.background(Color(0xFFFFEBEE)) }
                        •letIf(selected) { it.border(1.dp, Color(0xFF64B5F6)) }
                    """.trimIndent()
            )
        }
    }
}

//https://github.com/felix-leyva/SimpleViewModelExample.git (combine 3 category selection)
//https://github.com/puriakazemieh/Shop-Kotlin-kmp.git (Merge)